import { z } from 'zod';
/**
 * Worker taints.
 *
 * A worker may declare some of its own label KEYS as taints. A tainted
 * label `k=v` repels every build that does not explicitly require
 * `k=v` in `labels.requires` (the build's hard requirements double as
 * its tolerations). Workers without taints accept any build whose
 * `requires` they satisfy — the historical behaviour.
 *
 * The scheduling rule itself lives in the scheduler
 * (`planning/taints.ts`). This module only holds the shape and the
 * cross-field validation shared by the master API and the CLI.
 */
/** Maximum number of taints a worker may declare. */
export const MAX_WORKER_TAINTS = 32;
/**
 * Shape of a worker's `taints` field: a list of label keys. Duplicates
 * are collapsed (first occurrence wins) so `--taints a --taints a`
 * is harmless.
 */
export const WorkerTaintsSchema = z
    .array(z.string().trim().min(1).max(255))
    .max(MAX_WORKER_TAINTS)
    .transform(uniqueTaints);
/** Remove duplicate taint keys, preserving first-occurrence order. */
export function uniqueTaints(taints) {
    return [...new Set(taints)];
}
/**
 * Taint keys that do not exist among `labels`' keys. A taint on a
 * label the worker does not advertise could never be tolerated, so
 * it is a configuration error. Empty array = valid.
 */
export function findUnknownTaints(labels, taints) {
    return taints.filter((k) => !Object.prototype.hasOwnProperty.call(labels, k));
}
/**
 * Human-readable message for unknown taints, shared by the CLI (fail
 * fast before registering) and the master API (400).
 */
export function unknownTaintsMessage(unknown) {
    const keys = unknown.map((k) => `"${k}"`).join(', ');
    return (`taint key(s) ${keys} must be among the worker's labels ` +
        `(a taint marks one of the worker's own labels as repelling)`);
}
//# sourceMappingURL=taints.js.map